import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";

export default function LedProductList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [envFilter, setEnvFilter] = useState("All");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError("");
    const { data, error: fetchErr } = await supabase
      .from("led_products")
      .select("*")
      .order("sort_order", { ascending: true });
    if (fetchErr) {
      setError(fetchErr.message);
    } else {
      setProducts(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return products.filter((p) => {
      const matchSearch = !q || p.name?.toLowerCase().includes(q) || p.id?.toLowerCase().includes(q);
      const matchEnv = envFilter === "All" || p.environment === envFilter;
      return matchSearch && matchEnv;
    });
  }, [products, search, envFilter]);

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const { error: delErr } = await supabase.from("led_products").delete().eq("id", deleteTarget.id);
    setDeleting(false);
    if (delErr) {
      setError(delErr.message);
      return;
    }
    setProducts((prev) => prev.filter((p) => p.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  const toggleActive = async (product) => {
    const { error: updErr } = await supabase
      .from("led_products")
      .update({ active: !product.active })
      .eq("id", product.id);
    if (updErr) {
      setError(updErr.message);
      return;
    }
    setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, active: !p.active } : p)));
  };

  return (
    <div style={{ padding: "24px 28px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>Produk LED</div>
          <div style={{ fontSize: 13, color: "#94a3b8" }}>
            {products.length} total produk untuk LED Configurator — kelola spesifikasi yang tampil di canvas &amp; export PDF di sini.
          </div>
        </div>
        <Link
          to="/admin/led-products/new"
          style={{
            display: "flex", alignItems: "center", gap: 6, padding: "9px 16px",
            background: "#3b82f6", color: "#fff", borderRadius: 8, fontSize: 13,
            fontWeight: 600, textDecoration: "none", flexShrink: 0,
          }}
        >
          + Tambah Produk LED
        </Link>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cari nama atau ID produk..."
          style={{ flex: "1 1 240px", padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13, outline: "none" }}
        />
        <select
          value={envFilter}
          onChange={(e) => setEnvFilter(e.target.value)}
          style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13 }}
        >
          <option value="All">Semua Environment</option>
          <option value="indoor">Indoor</option>
          <option value="outdoor">Outdoor</option>
        </select>
      </div>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: 13, padding: "10px 14px", borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>Memuat produk...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
            Belum ada produk LED. Klik "+ Tambah Produk LED" untuk mulai, atau jalankan <code>supabase/schema_led_configurator.sql</code> dulu kalau tabelnya belum ada.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={thStyle}>Produk</th>
                <th style={thStyle}>Environment</th>
                <th style={thStyle}>Pixel Pitch</th>
                <th style={thStyle}>Cabinet (mm)</th>
                <th style={thStyle}>Spesifikasi Tambahan</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, color: "#0f172a" }}>{p.name}</div>
                    {p.series && <div style={{ fontSize: 11, color: "#64748b" }}>{p.series}</div>}
                    <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace" }}>{p.id}</div>
                  </td>
                  <td style={{ ...tdStyle, textTransform: "capitalize" }}>{p.environment}</td>
                  <td style={tdStyle}>P{p.pixel_pitch_mm}</td>
                  <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: 12 }}>{p.cabinet_width_mm}×{p.cabinet_height_mm}</td>
                  <td style={tdStyle}>
                    {Array.isArray(p.extra_specs) && p.extra_specs.length > 0 ? (
                      <span style={{ fontSize: 11, color: "#166534", background: "#dcfce7", padding: "2px 8px", borderRadius: 999, fontWeight: 600 }}>
                        {p.extra_specs.length} field
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>belum diisi</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => toggleActive(p)}
                      style={{
                        padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                        border: "none", cursor: "pointer",
                        background: p.active ? "#dcfce7" : "#f1f5f9",
                        color: p.active ? "#166534" : "#64748b",
                      }}
                    >
                      {p.active ? "Aktif" : "Nonaktif"}
                    </button>
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    <Link to={`/admin/led-products/${p.id}`} style={{ fontSize: 12, fontWeight: 600, color: "#3b82f6", textDecoration: "none", marginRight: 14 }}>
                      Edit
                    </Link>
                    <button
                      onClick={() => setDeleteTarget(p)}
                      style={{ fontSize: 12, fontWeight: 600, color: "#ef4444", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                    >
                      Hapus
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {deleteTarget && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, width: 360 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>Hapus produk LED?</div>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>
              <strong>{deleteTarget.name}</strong> akan dihapus permanen. Tindakan ini tidak bisa dibatalkan.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteTarget(null)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Batal
              </button>
              <button onClick={handleDeleteConfirmed} disabled={deleting} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#ef4444", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                {deleting ? "Menghapus..." : "Hapus"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = { textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 };
const tdStyle = { padding: "10px 14px", color: "#334155" };
